import { projectTimezone } from '@dashboard/utils/projectTimezone'
import api from '@dashboard/services/axiosApi'
import type { ReportingDateRange } from '@dashboard/components/common/dateRange'
import type { TeamPid } from '@dashboard/types/config'

export type ReportTeamPid = TeamPid | 'demo'

export type MetricBreakdown = {
  offerId?: string
  questionId?: string
  visitorsExposed?: number
  visitorsConverted?: number
  timeSeries?: { visitorsConverted: number }[]
}

export type DashboardCoreData = {
  previousPeriod?: DashboardCoreData
  totalVisitorOffersConverted?: number
  totalAnswers?: number
  totalNewEnriches?: number
  offersData?: { offerBreakdowns?: MetricBreakdown[] }
  questionsData?: { questionBreakdowns?: MetricBreakdown[] }
  toBeUsedForNewEnrichesData?: {
    syncUpsData?: { timeSeries?: { dataPointsEnriched?: number }[] }
  }
  conversions?: { result: number; holdback: boolean }[]
  exposures?: { result: number; holdback: boolean }[]
}

const calculatePreviousPeriod = (dateRange: ReportingDateRange) => {
  const periodLength = dateRange.end.getTime() - dateRange.start.getTime()
  return {
    start: new Date(dateRange.start.getTime() - periodLength),
    end: new Date(dateRange.start)
  }
}

export const fetchCoreData = async (
  teamPid: ReportTeamPid,
  dateRange: ReportingDateRange
): Promise<DashboardCoreData> => {
  const timezone = dateRange.timezone ?? projectTimezone()
  const params = {
    start: dateRange.start.toISOString(),
    end: dateRange.end.toISOString(),
    timezone
  }
  const previousPeriod = calculatePreviousPeriod(dateRange)
  const prevParams = {
    start: previousPeriod.start.toISOString(),
    end: previousPeriod.end.toISOString(),
    timezone
  }

  // Fire both requests in parallel — they have zero data dependencies
  // Personalization lift data (conversions/exposures) is now included in dashboardCoreData
  const [coreResult, previousPeriodResult] = await Promise.allSettled([
    api.get<DashboardCoreData>(`/api/${teamPid}/metrics/dashboardCoreData`, { params }),
    api.get<DashboardCoreData>(`/api/${teamPid}/metrics/dashboardCoreData`, { params: prevParams })
  ])

  const coreData = coreResult.status === 'fulfilled' ? coreResult.value.data : {}
  const previousPeriodData = previousPeriodResult.status === 'fulfilled' ? previousPeriodResult.value.data : {}

  return { ...coreData, previousPeriod: previousPeriodData }
}

export const fetchDataLastReceived = async (teamPid: ReportTeamPid) => {
  const { data } = await api.get(`/api/${teamPid}/metrics/dataLastReceived`)

  return data
}

export type AnalyticsCount = {
  count: number
}

export type WebAnalyticsData = {
  visitorType?: {
    anonymous?: number
    subscribers?: number
    suspectedBots?: number
  }
  trafficSources?: (AnalyticsCount & { channel: string })[]
  referrers?: (AnalyticsCount & { domain: string })[]
  landingPages?: (AnalyticsCount & { page: string })[]
  mostViewedPages?: (AnalyticsCount & { page: string })[]
  utmCampaigns?: (AnalyticsCount & { value: string })[]
  utmSources?: (AnalyticsCount & { value: string })[]
  utmMediums?: (AnalyticsCount & { value: string })[]
  geographic?: {
    countries?: (AnalyticsCount & { name: string })[]
    regions?: (AnalyticsCount & { name: string })[]
    cities?: (AnalyticsCount & { name: string })[]
  }
  languages?: (AnalyticsCount & { name: string })[]
  deviceTypes?: (AnalyticsCount & { type: string })[]
  operatingSystems?: (AnalyticsCount & { name: string })[]
  browsers?: (AnalyticsCount & { name: string })[]
}

// Web Analytics - single endpoint for all analytics data
export const fetchWebAnalytics = async (
  teamPid: ReportTeamPid,
  dateRange: ReportingDateRange,
  options: { sections?: string[]; limit?: number } = {}
): Promise<WebAnalyticsData> => {
  const params: Record<string, unknown> = {
    start: dateRange.start.toISOString(),
    end: dateRange.end.toISOString(),
    timezone: dateRange.timezone ?? projectTimezone()
  }

  if (options.sections?.length) {
    params.sections = options.sections
  }

  if (typeof options.limit === 'number') {
    params.limit = options.limit
  }

  const { data } = await api.get(`/api/${teamPid}/metrics/webAnalytics`, {
    params
  })
  return data
}
